/**
 * src/components/Financial/WeeklyCalendarSection.jsx
 * 【完全版：ダークモード対応・スタイリッシュUI・二十四節気追加】
 */

import React, { useState, useMemo, useEffect } from "react";
import * as Astro from "../../astronomy.js";
import {
  PLANET_SYMBOLS,
  DEFAULT_PLANET_COLORS,
  DEFAULT_SIGN_COLORS,
} from "../../utils/constants.js";
import { styles as commonStyles } from "../../utils/styles.js";
import { Calendar, Star, ChevronLeft, ChevronRight } from "lucide-react";

export default function WeeklyCalendarSection(props) {
  var baseDate = props.baseDate;
  var natalPositions = props.natalPositions;
  var isHeliocentric = props.isHeliocentric;
  var isSidereal = props.isSidereal;
  var showExtendedPoints = props.showExtendedPoints;
  var planetColors = props.planetColors || DEFAULT_PLANET_COLORS;
  var signColors = props.signColors || DEFAULT_SIGN_COLORS;
  var isDarkMode = props.isDarkMode;

  // ダークモード判定
  const isDark = (function () {
    const p = props.isDarkMode;
    if (p === true || p === "true" || p === "dark") return true;
    if (typeof document !== "undefined") {
      var el = document.documentElement;
      var bd = document.body;
      if (
        el.classList.contains("dark") ||
        bd.classList.contains("dark") ||
        el.getAttribute("data-theme") === "dark"
      )
        return true;
    }
    return false;
  })();

  const theme = {
    cardBg: isDark ? "#1c1f26" : "#ffffff",
    subBg: isDark ? "rgba(255, 255, 255, 0.03)" : "#fcfcfc",
    border: isDark ? "#2d313d" : "#eee",
    text: isDark ? "#ffffff" : "#1e293b",
    subText: isDark ? "#9ca3af" : "#64748b",
  };

  const [weekOffset, setWeekOffset] = useState(0);
  const [importantDates, setImportantDates] = useState({});
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768,
  );

  useEffect(function () {
    const handleResize = function () {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(function () {
    const saved = localStorage.getItem("astronica_important_dates");
    if (saved) setImportantDates(JSON.parse(saved));
  }, []);

  const toggleImportantDate = function (dateStr) {
    const newStatus = Object.assign({}, importantDates);
    newStatus[dateStr] = !newStatus[dateStr];
    setImportantDates(newStatus);
    localStorage.setItem(
      "astronica_important_dates",
      JSON.stringify(newStatus),
    );
  };

  const weekStartDate = useMemo(
    function () {
      var d = new Date(baseDate || new Date());
      var day = d.getDay();
      var diff = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + diff + weekOffset * 7);
      return d;
    },
    [baseDate, weekOffset],
  );

  const calendar = useMemo(
    function () {
      const cal = Astro.getWeeklyCalendar(
        weekStartDate,
        natalPositions || null,
        isHeliocentric === true,
        isSidereal === true,
        showExtendedPoints === true,
      );

      // 食リストの取得 (前後1年分)
      const eclipseList = Astro.getEclipses
        ? Astro.getEclipses(weekStartDate, 365)
        : [];

      return cal.map(function (day) {
        const curDate = day.date;
        const dateStr = curDate.toLocaleDateString("ja-JP");

        // --- 1. 新月・満月の判定 (astronomy-engine の月相を利用) ---
        const moonPhase = Astro.getMoonPhase(curDate);
        const prevDate = new Date(curDate.getTime() - 86400000);
        const prevMoonPhase = Astro.getMoonPhase(prevDate);

        // 新月判定 (角度が0を跨ぐとき)
        if (prevMoonPhase.angle > 330 && moonPhase.angle < 30) {
          day.events.unshift({
            type: "lunation",
            name: "新月 (New Moon)",
            symbol: "🌑",
            label: "Moon",
          });
        }
        // 満月判定 (角度が180を跨ぐとき)
        else if (prevMoonPhase.angle < 180 && moonPhase.angle >= 180) {
          day.events.unshift({
            type: "lunation",
            name: "満月 (Full Moon)",
            symbol: "🌕",
            label: "Moon",
          });
        }

        // --- 2. 食 (Eclipse) の判定 ---
        const dayEclipse = eclipseList.find(function (ecl) {
          return new Date(ecl.date).toLocaleDateString("ja-JP") === dateStr;
        });

        if (dayEclipse) {
          // 名称の重複（金環日食 金環 など）を避けるロジック
          const typeName = dayEclipse.type || "";
          const kindName = dayEclipse.japaneseKind || "";
          const displayName =
            typeName.indexOf(kindName) !== -1
              ? typeName
              : typeName + " " + kindName;

          day.events.unshift({
            type: "lunation",
            name: displayName.trim(),
            symbol: dayEclipse.symbol || "⚠️",
            label: "Eclipse",
          });
        }

        // --- 3. 重複排除 ---
        const seen = {};
        day.events = day.events.filter(function (e) {
          const normKey = (e.name || "") + (e.time || "");
          if (seen[normKey]) return false;
          seen[normKey] = true;
          return true;
        });

        return day;
      });
    },

    [
      weekStartDate,
      natalPositions,
      isHeliocentric,
      isSidereal,
      showExtendedPoints,
    ],
  );

  const renderEventCard = function (event, idx, dayDate) {
    const evName = event.name || "";
    const isCombust =
      event.type === "combust" ||
      evName.includes("Combust") ||
      evName.includes("コンバスト");
    const isStellium =
      evName.includes("Stellium") || evName.includes("ステリウム");

    var p1 = "";
    var p2 = "";
    // 初期値として event.planets を取得。なければ空配列
    let planetsArr = event.planets ? [...event.planets] : [];

    if (isCombust) {
      p1 = "Sun";
      p2 =
        event.planet ||
        event.planet2 ||
        event.p2 ||
        (planetsArr.length > 0 ? planetsArr[0] : "");
      if (p2 === "Sun" && planetsArr.length >= 2) p2 = planetsArr[1];
      planetsArr = [p1, p2];
    } else if (isStellium) {
      if (planetsArr.length === 0 && event.planet) planetsArr = [event.planet];
    } else {
      p1 = event.planet1 || event.p1 || event.planet || "";
      p2 = event.planet2 || event.p2 || "";
      if (planetsArr.length === 0 && p1) {
        planetsArr = p2 ? [p1, p2] : [p1];
      }
    }

    // ヘリオ/ジオのフィルタリング
    if (isHeliocentric === true) {
      // 日心図では月・太陽に関連するイベント（月相・節気等）を表示しない
      if (p1 === "Moon" || p2 === "Moon" || p1 === "Sun" || p2 === "Sun")
        return null;
      if (planetsArr.indexOf("Moon") !== -1 || planetsArr.indexOf("Sun") !== -1)
        return null;
      if (["dignity", "lunation", "solarTerm"].indexOf(event.type) !== -1)
        return null;
    } else {
      // 地心図では地球（Earth）に関連するイベントを表示しない
      if (p1 === "Earth" || p2 === "Earth") return null;
      if (planetsArr.indexOf("Earth") !== -1) return null;
    }

    const typeConfigs = {
      ingress: {
        border: "#3f51b5",
        bg: "rgba(63, 81, 181, 0.1)",
        icon: "✨",
        label: "Ingress",
      },
      aspect: {
        border: "#e67e22",
        bg: "rgba(230, 126, 34, 0.1)",
        icon: "💎",
        label: "Aspect",
      },
      transit: {
        border: "#e67e22",
        bg: "rgba(230, 126, 34, 0.1)",
        icon: "💎",
        label: "Aspect",
      },
      combust: {
        border: "#e74c3c",
        bg: "rgba(231, 76, 60, 0.1)",
        icon: "🔥",
        label: "Combust",
      },
      pattern: {
        border: "#9b59b6",
        bg: "rgba(155, 89, 182, 0.1)",
        icon: "🌌",
        label: "Pattern",
      },
      lunation: {
        // label が "Eclipse" の場合は警告色（オレンジ）、それ以外は月相色
        border: event.label === "Eclipse" ? "#f39c12" : "#607d8b",
        bg:
          event.label === "Eclipse"
            ? "rgba(243, 156, 18, 0.15)"
            : "rgba(96, 125, 139, 0.1)",
        icon: event.label === "Eclipse" ? "⚠️" : "🌙",
        label: event.label || "Moon",
      },
      solarTerm: {
        border: "#27ae60",
        bg: "rgba(39, 174, 96, 0.1)",
        icon: "🌿",
        label: "Season",
      },
      dignity: {
        border: "#c77a0a",
        bg: "rgba(199, 122, 10, 0.1)",
        icon: "☀️",
        label: "Dignity",
      },
      station: {
        border: "#2ecc71",
        bg: "rgba(46, 204, 113, 0.1)",
        icon: "🔄",
        label: "Station",
      },
      voc: {
        border: "#f39c12",
        bg: "rgba(243, 156, 18, 0.1)",
        icon: "🚫",
        label: "VOC",
      },
      oob: {
        border: "#e74c3c",
        bg: "rgba(231, 76, 60, 0.1)",
        icon: "⚠️",
        label: "OOB",
      },
      sukuyo: {
        border: "#8b5cf6",
        bg: "rgba(139, 92, 246, 0.1)",
        icon: "🌙",
        label: "宿曜",
      },
    };

    const config = typeConfigs[event.type] || {
      border: "#ccc",
      bg: "transparent",
      icon: "•",
      label: "Event",
    };
    const eventKey =
      "ev-" + (dayDate instanceof Date ? dayDate.getTime() : idx) + "-" + idx;

    return (
      <div
        key={eventKey}
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "12px 10px" : "10px 16px",
          borderRadius: "10px",
          background: isDark ? "rgba(255, 255, 255, 0.03)" : "#fff",
          border: "1px solid " + theme.border,
          borderLeft: isMobile
            ? "1px solid " + theme.border
            : "5px solid " + config.border,
          borderTop: isMobile
            ? "4px solid " + config.border
            : "1px solid " + theme.border,
          marginBottom: "8px",
          width: "100%",
          boxSizing: "border-box",
          gap: isMobile ? "10px" : "15px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            gap: isMobile ? "6px" : "15px",
            flex: 1,
            width: "100%",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minWidth: isMobile ? "auto" : "85px",
              justifyContent: isMobile ? "center" : "flex-start",
              flexWrap: "wrap",
            }}
          >
            {(event.type === "aspect" ||
              event.type === "transit" ||
              isCombust ||
              isStellium) &&
            planetsArr.length > 0 ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {planetsArr.map((pName, pIdx) => (
                  <React.Fragment key={pIdx}>
                    <span
                      style={{
                        fontSize: isMobile ? "24px" : "20px",
                        color: planetColors[pName] || theme.text,
                        lineHeight: 1,
                      }}
                    >
                      {PLANET_SYMBOLS[pName] || pName}
                    </span>
                    {pIdx < planetsArr.length - 1 && (
                      <span
                        style={{
                          fontSize: "16px",
                          color: isDark ? "rgba(255,255,255,0.4)" : "#64748b",
                          fontWeight: "bold",
                        }}
                      >
                        {!isCombust && !isStellium && event.symbol
                          ? event.symbol
                          : "-"}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : event.type === "ingress" ? (
              <>
                <span
                  style={{
                    fontSize: "18px",
                    color: planetColors[p1] || theme.text,
                  }}
                >
                  {PLANET_SYMBOLS[p1] || "•"}
                </span>
                <span style={{ fontSize: "14px", color: theme.subText }}>
                  →
                </span>
                <span
                  style={{
                    fontSize: "18px",
                    color:
                      (event.toSign && signColors[event.toSign.symbol]) ||
                      theme.text,
                  }}
                >
                  {(event.toSign ? event.toSign.symbol : "") + "\uFE0E"}
                </span>
              </>
            ) : (
              <span style={{ fontSize: isMobile ? "24px" : "20px" }}>
                {event.symbol || config.icon}
              </span>
            )}
          </div>

          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: isMobile ? "12px" : "13px",
                fontWeight: "bold",
                color: theme.text,
              }}
            >
              {isCombust ? `${p1} combust ${p2}` : event.name}
            </div>
            {event.time && (
              <div
                style={{
                  fontSize: "10px",
                  color: theme.subText,
                  marginTop: isMobile ? "4px" : "2px",
                  fontWeight: "bold",
                }}
              >
                {event.time}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            fontSize: "9px",
            fontWeight: "bold",
            padding: "2px 8px",
            borderRadius: "4px",
            background: config.bg,
            color: config.border,
            border: "1px solid " + config.border + "33",
            alignSelf: isMobile ? "center" : "center",
          }}
        >
          {config.label}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        borderRadius: "15px",
        border: "1px solid " + theme.border,
        padding: "25px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isMobile === true ? "column" : "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          gap: isMobile === true ? "12px" : "0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "16px",
            fontWeight: "bold",
            color: theme.text,
          }}
        >
          <Calendar size={18} /> <span>週間イベントカレンダー</span>
        </div>
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: isMobile === true ? "center" : "flex-end",
            width: isMobile === true ? "100%" : "auto",
          }}
        >
          <button
            onClick={function () {
              setWeekOffset(weekOffset - 1);
            }}
            style={{
              padding: "8px 20px",
              background: theme.subBg,
              border: "1px solid " + theme.border,
              color: theme.text,
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={function () {
              setWeekOffset(weekOffset + 1);
            }}
            style={{
              padding: "8px 20px",
              background: theme.subBg,
              border: "1px solid " + theme.border,
              color: theme.text,
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
        {calendar.map(function (day) {
          const dateStr = day.date.toLocaleDateString("ja-JP");
          const isToday = dateStr === new Date().toLocaleDateString("ja-JP");
          const isImportant = importantDates[dateStr] === true;

          return (
            <div
              key={"day-" + dateStr}
              style={{
                display: "flex",
                flexDirection: isMobile === true ? "column" : "row",
                background: isImportant
                  ? isDark
                    ? "rgba(234, 179, 8, 0.05)"
                    : "#fffdef"
                  : theme.subBg,
                borderRadius: "12px",
                border:
                  "1px solid " +
                  (isImportant
                    ? isDark
                      ? "rgba(234, 179, 8, 0.3)"
                      : "#f9d97f"
                    : theme.border),
                // ★修正：パディングをモバイル向けに最適化
                padding: isMobile === true ? "16px" : "15px",
                gap: isMobile === true ? "15px" : "20px",
                alignItems: "flex-start",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: isMobile === true ? "100%" : "140px",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <button
                    onClick={function () {
                      toggleImportantDate(dateStr);
                    }}
                    style={{
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      color: isImportant ? "#f1c40f" : "#ccc",
                      padding: 0,
                    }}
                  >
                    <Star size={18} fill={isImportant ? "#f1c40f" : "none"} />
                  </button>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "bold",
                      color: isToday
                        ? isDark
                          ? "#60a5fa"
                          : "#2c2d96"
                        : theme.text,
                    }}
                  >
                    {day.date.getMonth() +
                      1 +
                      "/" +
                      day.date.getDate() +
                      "(" +
                      day.dayOfWeek +
                      ")"}
                  </div>
                </div>
                {isToday && (
                  <div
                    style={{
                      marginTop: "6px",
                      display: "inline-block",
                      padding: "2px 8px",
                      backgroundColor: isDark ? "#60a5fa" : "#2c2d96",
                      color: "#fff",
                      borderRadius: "4px",
                      fontSize: "10px",
                      fontWeight: "900",
                      letterSpacing: "0.05em",
                      marginLeft: "26px",
                    }}
                  >
                    TODAY
                  </div>
                )}
              </div>

              <div style={{ flex: 1, width: "100%" }}>
                {day.events.length > 0 ? (
                  day.events.map(function (e, idx) {
                    return renderEventCard(e, idx, day.date);
                  })
                ) : (
                  <div
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: theme.subText,
                      fontSize: "12px",
                      background: "transparent",
                      borderRadius: "10px",
                      border: "1px dashed " + theme.border,
                      fontWeight: "bold",
                    }}
                  >
                    天体イベントなし
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          marginTop: "25px",
          paddingTop: "20px",
          borderTop: "1px solid " + theme.border,
        }}
      >
        <button
          onClick={function () {
            setWeekOffset(weekOffset - 1);
          }}
          style={{
            padding: "10px 30px",
            display: "flex",
            alignItems: "center",
            background: theme.subBg,
            border: "1px solid " + theme.border,
            color: theme.text,
            borderRadius: "8px",
            cursor: "pointer",
          }}
        >
          <ChevronLeft size={18} />
          <span
            style={{ marginLeft: "8px", fontSize: "13px", fontWeight: "bold" }}
          >
            前週
          </span>
        </button>
        <button
          onClick={function () {
            setWeekOffset(weekOffset + 1);
          }}
          style={{
            padding: "10px 30px",
            display: "flex",
            alignItems: "center",
            background: theme.subBg,
            border: "1px solid " + theme.border,
            color: theme.text,
            borderRadius: "8px",
            cursor: "pointer",
          }}
        >
          <span
            style={{ marginRight: "8px", fontSize: "13px", fontWeight: "bold" }}
          >
            翌週
          </span>
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}
